import { EXTRACT_OCR_STATUS } from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import {
  Auction,
  ClinicalConsultation,
  ExtractOCRStatus,
  ExtractOCRStatusResult,
  FileState,
  Patient,
} from "core/types";
// todo move this hook out of model
// eslint-disable-next-line no-outside-core-imports/no-outside-core-imports
import { isFileV2 } from "dsl/organisms/FileTables";
import { ReactNode, createContext, useContext, useMemo } from "react";
import { useExtractOCR } from "./hooks/extract/ocr";
import { useExtractOCRStatus } from "./hooks/extract/status";

export type ExtractDocsContextValue = {
  extractOcr: ReturnType<typeof useExtractOCR>;
  extractOcrStatus: ReturnType<typeof useExtractOCRStatus>;
  utils: {
    getExtractedDocument: (
      file: FileState,
    ) => ExtractOCRStatusResult | undefined;
    isFileExtracted: (file: FileState) => boolean;
    isFileExtractionFailed: (file: FileState) => boolean;
    isFileExtractionProcessing: (file: FileState) => boolean;
  };
};

const emptyFunction = (functionName: string) => () => {
  throw new Error(`${functionName} must be used within an ExtractDocsProvider`);
};

const emptyExtractDocsContextValue: ExtractDocsContextValue = {
  extractOcr: {
    errors: [],
    extractingAllFiles: false,
    handleDownloadAndRunOcr: emptyFunction("handleDownloadAndRunOcr"),
    handleOcrForFile: emptyFunction("handleOcrForFile"),
    processingFileIds: [],
    results: {},
  },
  extractOcrStatus: {
    error: null,
    isInitialResponseReceived: false,
    refetch: emptyFunction("refetch"),
    result: null,
  },
  utils: {
    getExtractedDocument: emptyFunction("getExtractedDocument"),
    isFileExtracted: emptyFunction("isFileExtracted"),
    isFileExtractionFailed: emptyFunction("isFileExtractionFailed"),
    isFileExtractionProcessing: emptyFunction("isFileExtractionProcessing"),
  },
};

const ExtractDocsContext = createContext<ExtractDocsContextValue | undefined>(
  undefined,
);

type ExtractDocsProviderProps = {
  auction?: Auction;
  consultation?: ClinicalConsultation;
  children: ReactNode;
  patient: Patient;
};

const PROCESSING_STATUS: ExtractOCRStatus[] = [
  EXTRACT_OCR_STATUS.PROCESSING,
  EXTRACT_OCR_STATUS.PENDING,
];

export function getExtractedDocumentFromFile(
  documents: ExtractOCRStatusResult[] | undefined,
  file: FileState,
): ExtractOCRStatusResult | undefined {
  return documents?.find((doc) => {
    if (!file?.id) return false;
    // check if the file is a recare file
    if (doc.recare_file_id === file.id) return true;
    // check if the file is a HIS file
    if (doc.kis_file_id === file.id) return true;
    // check if the file is a HIS file that has been uploaded to our BE
    if (
      isFileV2(file) &&
      file.kis_document_id_string &&
      doc.kis_file_id === file.kis_document_id_string
    )
      return true;

    return false;
  });
}

export const ExtractDocsProvider = ({
  auction,
  children,
  consultation,
  patient,
}: ExtractDocsProviderProps) => {
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();
  const extractOcrStatus = useExtractOCRStatus({
    auctionId: auction?.id,
    careseekerId: patient.careseeker?.id,
    consultationId: consultation?.id,
    externalId: patient.external_id,
    patientId: patient.id,
    skip: !isDocsAndExtractActive && !isDischargeLetterActive,
  });
  const extractOcr = useExtractOCR({
    auctionId: auction?.id,
    careseekerId: patient.careseeker?.id,
    consultationId: consultation?.id,
    externalId: patient.external_id,
    ocrStatus: extractOcrStatus.result,
    onComplete: extractOcrStatus.refetch,
    patientId: patient.id,
  });

  const getExtractedDocument = (
    file: FileState,
  ): ExtractOCRStatusResult | undefined => {
    return getExtractedDocumentFromFile(
      extractOcrStatus.result?.documents,
      file,
    );
  };

  // need to check kis_document_id for his files if it has been created in our BE
  const isFileExtracted = (file: FileState): boolean => {
    return getExtractedDocument(file)?.status === EXTRACT_OCR_STATUS.EXTRACTED;
  };

  const isFileExtractionProcessing = (file: FileState): boolean => {
    const document = getExtractedDocument(file);
    if (!document) {
      return extractOcr.processingFileIds.includes(file.id);
    }

    return PROCESSING_STATUS.includes(document.status);
  };

  const isFileExtractionFailed = (file: FileState): boolean => {
    return getExtractedDocument(file)?.status === EXTRACT_OCR_STATUS.FAILED;
  };

  const utils = {
    getExtractedDocument,
    isFileExtracted,
    isFileExtractionFailed,
    isFileExtractionProcessing,
  };

  const value: ExtractDocsContextValue = useMemo(
    () => ({
      extractOcr,
      extractOcrStatus,
      utils,
    }),
    [extractOcr, extractOcrStatus, utils],
  );

  return (
    <ExtractDocsContext.Provider value={value}>
      {children}
    </ExtractDocsContext.Provider>
  );
};

export const useExtractDocs = (): ExtractDocsContextValue => {
  const context = useContext(ExtractDocsContext);

  if (context === undefined) {
    throw new Error(
      "useExtractDocs must be used within an ExtractDocsProvider",
    );
  }

  return context;
};

export const MockExtractDocsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  return (
    <ExtractDocsContext.Provider value={emptyExtractDocsContextValue}>
      {children}
    </ExtractDocsContext.Provider>
  );
};
